import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { WebSocketContext } from '../components/webSocketConnection/WebSocket'
import { constructCalendarDataLookup, getTradingSessionsForDate } from '../util/chart'
import { toDayStart } from '../util/dateTime'
import { TradingSession } from '../proto/generated/trading-calendar'
import { selectTradingCalendarDataByExchange } from '../selectors/chart'
import { Exchange } from '../proto/generated/common'

export const useIsStaleHook = (durationInSeconds: number, exchange: Exchange) => {
    const ws = useContext(WebSocketContext)
    const tradingCalendarData = useSelector(selectTradingCalendarDataByExchange(exchange))
    const lastUpdateSocketTime = useRef(0)
    const [isStale, setIsStale] = useState(false)
    const tradingSessionsRef = useRef<TradingSession[]>([])

    useEffect(() => {
        tradingSessionsRef.current = getTradingSessionsForDate(Math.floor(Date.now() / 1000), constructCalendarDataLookup(tradingCalendarData))
    }, [tradingCalendarData])

    const websocketListener = useCallback(() => {
        lastUpdateSocketTime.current = Date.now()
    }, [])

    useEffect(() => {
        ws?.addListener(websocketListener)
        return () => {
            ws?.removeListener(websocketListener)
        }
    }, [websocketListener])

    const windowFocusListener = useCallback(() => {
        let open = false
        const dayStart = toDayStart(Date.now()).getTime() / 1000
        const currentTimeInSeconds = Math.floor(Date.now() / 1000)
        const tradingSessions = tradingSessionsRef.current
        for (let c = 0; c < tradingSessions.length; c++) {
            const session = tradingSessions[c]
            if (dayStart + session.startTime <= currentTimeInSeconds && currentTimeInSeconds < dayStart + session.endTime) {
                open = true
                break
            }
        }

        if (!open || !lastUpdateSocketTime.current) return

        if (Date.now() - lastUpdateSocketTime.current >= durationInSeconds * 1000) {
            setIsStale(true)
        }
    }, [])

    useEffect(() => {
        window.addEventListener('focus', windowFocusListener)
        return () => window.removeEventListener('focus', windowFocusListener)
    }, [])

    return isStale
}
